<template>
    <div
        class="modal fade"
        id="weaponCompendiumModal"
        tabindex="-1"
        aria-hidden="true"
        ref="modal"
    >
        <div class="modal-dialog modal-lg modal-dialog-centered">
            <div class="modal-content bg-dark text-light rounded-3 shadow-lg">
                <div class="modal-header">
                    <h4 class="modal-title">
                        {{ mode === "add" ? "Add Weapon" : "Edit Weapon" }}
                    </h4>
                    <button
                        type="button"
                        class="btn-close"
                        data-bs-dismiss="modal"
                    ></button>
                </div>

                <div class="modal-body">
                    <!-- Edit: pick existing -->
                    <div v-if="mode === 'edit'" class="input-group mb-3">
                        <span class="input-group-text">Select Weapon</span>
                        <select
                            class="form-select"
                            aria-describedby="weapon-type"
                            v-model="selectedWeapon"
                            @change="fillFromPreset"
                        >
                            <option value="">Select...</option>
                            <optgroup
                                v-for="[type, weapons] in groupedWeapons"
                                :key="type"
                                :label="type"
                            >
                                <option
                                    v-for="weapon in weapons"
                                    :key="weapon.name"
                                    :value="weapon.name"
                                >
                                    {{ weapon.name }}
                                    ({{
                                        weapon.archetype
                                            .map(
                                                (a) =>
                                                    a.charAt(0).toUpperCase() +
                                                    a.slice(1)
                                            )
                                            .join(", ")
                                    }})
                                </option>
                            </optgroup>
                        </select>
                    </div>

                    <!-- Weapon form -->
                    <div class="input-group mb-3">
                        <span class="input-group-text">Name</span>
                        <input
                            v-model="weapon.name"
                            type="text"
                            class="form-control"
                        />
                    </div>
                    <div class="input-group mb-3">
                        <span class="input-group-text">Description</span>
                        <textarea
                            v-model="weapon.description"
                            class="form-control"
                        ></textarea>
                    </div>

                    <div class="row">
                        <div class="col-sm-6">
                            <div class="input-group mb-3">
                                <span class="input-group-text">Damage</span>
                                <select
                                    v-model="weapon.damage"
                                    class="form-select"
                                >
                                    <option disabled value="">Select...</option>
                                    <option v-for="n in 10" :key="n" :value="n">
                                        {{ n }}
                                    </option>
                                </select>
                                <span class="input-group-text">Type</span>
                                <input
                                    v-model="weapon.damageType"
                                    type="text"
                                    class="form-control"
                                />
                            </div>
                        </div>
                        <div class="col-sm-6">
                            <div class="input-group mb-3">
                                <span class="input-group-text">Range</span>
                                <select
                                    v-model="weapon.range"
                                    class="form-select"
                                >
                                    <option disabled value="">Select...</option>
                                    <option value="Close (5ft)">
                                        Close (5ft)
                                    </option>
                                    <option value="Near (15ft)">
                                        Near (15ft)
                                    </option>
                                    <option value="Medium (50ft)">
                                        Medium (50ft)
                                    </option>
                                    <option value="Long (150ft)">
                                        Long (150ft)
                                    </option>
                                    <option value="Far (200ft)">
                                        Far (200ft)
                                    </option>
                                </select>
                            </div>
                        </div>
                    </div>

                    <!-- Archetypes -->
                    <div class="input-group mb-3">
                        <span class="input-group-text">Skill Type</span>
                        <select v-model="selectedArchetype" class="form-select">
                            <option disabled value="">Select...</option>
                            <option value="grit">Grit</option>
                            <option value="wit">Wit</option>
                            <option value="smarts">Smarts</option>
                            <option value="heart">Heart</option>
                            <option value="luck">Luck</option>
                            <option value="strange">Strange</option>
                        </select>
                        <button
                            class="btn btn-outline-primary"
                            type="button"
                            @click="addArchetype"
                        >
                            Add
                        </button>
                    </div>
                    <div
                        v-if="weapon.archetype.length"
                        class="d-flex flex-wrap gap-2 mb-3"
                    >
                        <span
                            v-for="(a, i) in weapon.archetype"
                            :key="i"
                            class="badge bg-primary d-flex align-items-center"
                        >
                            {{ a.charAt(0).toUpperCase() + a.slice(1) }}
                            <button
                                type="button"
                                class="btn-close btn-close-white ms-2"
                                style="font-size: 0.6rem"
                                @click="removeArchetype(i)"
                            ></button>
                        </span>
                    </div>

                    <!-- Abilities -->
                    <div class="input-group mb-3">
                        <span class="input-group-text">Ability</span>
                        <select v-model="selectedAbility" class="form-select">
                            <option disabled value="">Select...</option>
                            <option
                                v-for="a in store.weaponAbilities.sort((a, b) =>
                                    a.name.localeCompare(b.name)
                                )"
                                :key="a.name"
                                :value="a.name"
                            >
                                {{ a.name }}
                            </option>
                        </select>
                        <button
                            class="btn btn-outline-primary"
                            type="button"
                            @click="addAbility"
                        >
                            Add
                        </button>
                    </div>
                    <div class="input-group mb-3">
                        <span class="input-group-text">Custom Ability</span>
                        <input
                            v-model="customAbilityName"
                            type="text"
                            class="form-control"
                            placeholder="Name"
                        />
                        <input
                            v-model="customAbilityDesc"
                            type="text"
                            class="form-control"
                            placeholder="Description"
                        />
                        <button
                            class="btn btn-outline-primary"
                            type="button"
                            @click="addCustomAbility"
                        >
                            Add
                        </button>
                    </div>
                    <div
                        v-if="weapon.abilities.length"
                        class="d-flex flex-wrap gap-2"
                    >
                        <span
                            v-for="(ab, i) in weapon.abilities"
                            :key="i"
                            class="badge bg-success d-flex align-items-center"
                        >
                            {{ typeof ab === "object" ? ab.name : ab }}
                            <button
                                type="button"
                                class="btn-close btn-close-white ms-2"
                                style="font-size: 0.6rem"
                                @click="removeAbility(i)"
                            ></button>
                        </span>
                    </div>
                </div>

                <p class="px-3">
                    <strong
                        >Note: These changes won't affect any weapons currently
                        added to your character. You will need to remove and
                        re-add the weapon for the changes to take
                        effect.</strong
                    >
                </p>

                <div class="modal-footer">
                    <button class="btn btn-secondary" data-bs-dismiss="modal">
                        <font-awesome-icon icon="multiply" class="me-1" />Close
                    </button>
                    <button
                        v-if="mode === 'edit'"
                        class="btn btn-danger me-1"
                        @click="deleteWeapon"
                    >
                        <font-awesome-icon
                            icon="trash-alt"
                            class="me-1"
                        />Delete
                    </button>
                    <button class="btn btn-primary" @click="saveWeapon">
                        <font-awesome-icon icon="save" class="me-1" />Save
                    </button>
                </div>
            </div>
        </div>
    </div>
</template>

<script>
import { useRosterStore } from "@/stores/index";

export default {
    name: "WeaponCompendiumModal",
    props: { mode: { type: String, default: "add" } },
    data() {
        return {
            store: useRosterStore(),
            selectedWeapon: "", // ✅ renamed from selectedName
            weapon: {
                name: "",
                description: "",
                damage: "",
                damageType: "",
                range: "",
                archetype: [],
                abilities: [],
            },
            selectedArchetype: "",
            selectedAbility: "",
            customAbilityName: "",
            customAbilityDesc: "",
        };
    },
    computed: {
        groupedWeapons() {
            if (!this.store?.weapons) return [];

            // group by type
            const groups = this.store.weapons.reduce((acc, weapon) => {
                if (!acc[weapon.type]) acc[weapon.type] = [];
                acc[weapon.type].push(weapon);
                return acc;
            }, {});

            // sort each group by weapon name
            for (const type in groups) {
                groups[type].sort((a, b) => a.name.localeCompare(b.name));
            }

            // convert object → array and sort groups by type name
            return Object.entries(groups).sort((a, b) =>
                a[0].localeCompare(b[0])
            );
        }
    },
    mounted() {
        this.$refs.modal.addEventListener("show.bs.modal", () => {
            this.resetForm();
        });
    },
    watch: {
        // ✅ automatically load weapon when user selects one
        selectedWeapon(newVal) {
            if (newVal) this.loadWeapon();
        }
    },
    methods: {
        resetForm() {
            this.selectedWeapon = "";
            this.weapon = {
                name: "",
                description: "",
                damage: "",
                damageType: "",
                range: "",
                archetype: [],
                abilities: [],
            };
            this.selectedArchetype = "";
            this.selectedAbility = "";
            this.customAbilityName = "";
            this.customAbilityDesc = "";
        },
        loadWeapon() {
            const found = this.store.weapons.find(
                (w) => w.name === this.selectedWeapon
            );
            if (found) this.weapon = JSON.parse(JSON.stringify(found));
        },
        ensureUniqueAbilityName(name) {
            let newName = name;
            let counter = 65; // ASCII A
            while (this.store.weaponAbilities.some((a) => a.name === newName)) {
                newName = `${name} (${String.fromCharCode(counter)})`;
                counter++;
            }
            return newName;
        },
        saveWeapon() {
            if (!this.weapon.name.trim()) {
                alert("Weapon must have a name!");
                return;
            }
            if (!this.weapon.archetype.length) {
                alert("Weapon must have at least one skill type!");
                return;
            }
            if (
                !this.weapon.damage ||
                !this.weapon.damageType.trim() ||
                !this.weapon.range.trim()
            ) {
                alert("Weapon must have damage, damage type, and range!");
                return;
            }
            if (this.selectedAbility !== "") {
                alert(
                    "You selected an ability but didn’t add it. Please press Add first."
                );
                return;
            }
            if (
                this.customAbilityName.trim() ||
                this.customAbilityDesc.trim()
            ) {
                alert(
                    "You typed a custom ability but didn’t add it. Please press Add first."
                );
                return;
            }

            for (const ability of this.weapon.abilities) {
                if (ability.custom) {
                    const uniqueName = this.ensureUniqueAbilityName(
                        ability.name
                    );
                    const abilityToAdd = { ...ability, name: uniqueName };
                    delete abilityToAdd.custom;
                    this.store.weaponAbilities.push(abilityToAdd);
                }
            }

            // Transform weapon.abilities to an array of names
            this.weapon.abilities = this.weapon.abilities.map(
                (ability) => ability.name
            );

            if (this.mode === "add") {
                this.store.weapons.push(this.weapon);
                alert("Weapon added to compendium.");
            } else {
                const idx = this.store.weapons.findIndex(
                    (w) => w.name === this.selectedWeapon
                );
                if (idx !== -1) this.store.weapons.splice(idx, 1, this.weapon);
                alert("Weapon updated in compendium.");
            }
            this.resetForm();
        },
        deleteWeapon() {
            if (!this.selectedWeapon) return;
            if (confirm(`Delete weapon "${this.selectedWeapon}"?`)) {
                const idx = this.store.weapons.findIndex(
                    (w) => w.name === this.selectedWeapon
                );
                if (idx !== -1) this.store.weapons.splice(idx, 1);
                alert("Weapon deleted from compendium.");
            }
            this.resetForm();
        },
        addArchetype() {
            if (
                this.selectedArchetype &&
                !this.weapon.archetype.includes(this.selectedArchetype)
            ) {
                this.weapon.archetype.push(this.selectedArchetype);
            }
            this.selectedArchetype = "";
        },
        removeArchetype(i) {
            this.weapon.archetype.splice(i, 1);
        },
        addAbility() {
            if (!this.selectedAbility) return;
            const ab = this.store.weaponAbilities.find(
                (a) => a.name === this.selectedAbility
            );
            if (ab && !this.weapon.abilities.find((a) => a.name === ab.name)) {
                this.weapon.abilities.push({ ...ab, custom: false });
            }
            this.selectedAbility = "";
        },
        addCustomAbility() {
            if (
                !this.customAbilityName.trim() ||
                !this.customAbilityDesc.trim()
            ) {
                alert("Custom ability must have both Name and Description!");
                return;
            }
            this.weapon.abilities.push({
                name: this.customAbilityName.trim(),
                description: this.customAbilityDesc.trim(),
                custom: true,
            });
            this.customAbilityName = "";
            this.customAbilityDesc = "";
        },
        removeAbility(i) {
            this.weapon.abilities.splice(i, 1);
        },
    },
};
</script>


